'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Details from '@/features/cart/components/details.component';
import { selectCartItems, useCartStore } from '@/store/use-cart.store';
import { useRouter } from 'nextjs-toploader/app';
import { isDesktop } from 'react-device-detect';
import { useBasketCalculation } from '@/common/apis/cart/basket-calculation';
import { useBasketCheckAvailabilityMutation } from '@/common/apis/cart/basket-check-availability';
import AnalyticsWrapper from '@/common/components/analytics-wrapper';
import Button from '@/common/components/button';
import CardDivider from '@/common/components/card-divider';
import EmptyState from '@/common/components/empty-state/empty-state.component';
import FixedBottom from '@/common/components/fixed-bottom';
import Footer from '@/common/components/footer';
import Layout from '@/common/components/layout';
import Toast from '@/common/components/toast/toast.component';
import { appConfigs } from '@/common/configs/app.config';
import { useSession } from '@/common/hooks/use-session.hook';
import { calculateCartTotalCount } from '@/common/utils/cart-count.utility';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';
import createUrlUtility from '@/common/utils/create-url.utility';
import { rialToToman } from '@/common/utils/money-exchange.utility';
import { resolveStorefrontPath } from '@/common/utils/storefront-path.utility';
import AuthDrawer from './auth-drawer.component';
import AuthModal from './auth-modal.component';
import Item from './item.component';

interface MainProps {
  pageTitle: string;
}
// eslint-disable-next-line max-lines-per-function
export default function Main({ pageTitle }: MainProps) {
  const { status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const localCartItems = useCartStore(selectCartItems);
  const [type, setType] = useState('drawer');
  const componentRef = useRef<HTMLDivElement>(null);

  const [calculation, setCalculation] = useState({
    totalAmount: 0,
    totalAmountPayable: 0,
    totalDiscount: 0,
    totalShippingFee: 0,
    taxPercent: 0,
  });

  const [openAuthDrawer, setOpenAuthDrawer] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const { mutate: calculate, data, isSuccess, isIdle } = useBasketCalculation();

  const { mutate: basketCheckAvailability, data: basketCheckAvailabilityData } =
    useBasketCheckAvailabilityMutation();

  const count = useMemo(() => {
    return calculateCartTotalCount(localCartItems);
  }, [localCartItems]);

  const cartItem = useMemo(() => {
    if (localCartItems && localCartItems.length > 0) return localCartItems[0];
    else return null;
  }, [localCartItems]);

  const storefrontPath = resolveStorefrontPath(
    searchParams.get('from') ?? cartItem?.vendor.slug
  );

  const handleReturnToStorefront = () => {
    router.replace(storefrontPath);
  };

  useEffect(() => {
    if (componentRef.current && typeof window !== 'undefined') {
      const styles = window.getComputedStyle(componentRef.current);
      if (styles.display === 'none' || styles.visibility === 'hidden')
        setType('drawer');
      else setType('modal');
    }
  }, [componentRef.current?.clientWidth]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (data?.data) {
      const calculatedBasket = {
        totalAmount: rialToToman(
          data?.data?.totalAmount,
          appConfigs.rialExchangeRate
        ),
        totalAmountPayable: rialToToman(
          data?.data?.totalAmountPayable,
          appConfigs.rialExchangeRate
        ),
        totalDiscount: rialToToman(
          data?.data?.totalDiscount,
          appConfigs.rialExchangeRate
        ),
        totalShippingFee: rialToToman(
          data?.data?.totalShippingFee,
          appConfigs.rialExchangeRate
        ),
        taxPercent: data?.data?.taxPercent || 0,
      };

      setCalculation(calculatedBasket);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (localCartItems?.length > 0 && cartItem) {
      const ids = cartItem.products.map((pItem) => pItem.productVariantId);
      basketCheckAvailability(ids, {
        onSuccess(data) {
          const availableIds = data.data.response
            .filter((rItem) => rItem.productVariantAvailability)
            .map((rItem) => rItem.varaintId);
          const calculatableItems = cartItem.products
            .filter((pItem) => availableIds.includes(pItem.productVariantId))
            .map((pItem) => ({
              id: pItem.productVariantId,
              quantity: pItem.quantity,
            }));
          if (calculatableItems && calculatableItems.length > 0) {
            setCanSubmit(true);
            calculate(calculatableItems);
          } else setCanSubmit(false);
        },
      });
    }
  }, [localCartItems]);

  // Prevent hydration mismatch by only showing content after mount
  if (!isMounted) {
    return null;
  }

  if (count === 0) {
    return (
      <div className="fixed right-0 top-0 flex h-full w-full flex-col items-center justify-center">
        <EmptyState
          iconSrc={createUrlUtility({
            params: 'static/images/svg/empty-state-order.svg',
          })}
          className="!static my-4 !h-fit"
          message="سبد خریدت خالیه که!"
        />
        <Button onClick={handleReturnToStorefront} type="link">
          بازگشت به صفحه اصلی
        </Button>
      </div>
    );
  }

  const handleAuthDrawer = () => {
    setOpenAuthDrawer((open) => !open);
  };

  const clickHandler = () => {
    status === 'authenticated'
      ? canSubmit
        ? router.push(`/profile${createQueryStringUtility({ from: 'cart' })}`)
        : Toast.error('هیچ محصولی جهت ادامه سفارش یافت نشد')
      : handleAuthDrawer();
  };

  return (
    <>
      <Layout
        appbar={{
          title: pageTitle,
          cartWidget: false,
          showSearch: isDesktop,
        }}
        responsiveAppbar={true}
        className="lg:bg-silver-100"
      >
        <AnalyticsWrapper analytics={['checkout-view-cart-v1']} isPageLog>
          <div className="w-full overflow-y-auto lg:pb-[92px] lg:!mt-[48px]">
            <div className="flex flex-col items-center justify-between bg-silver-100 lg:mx-auto lg:!grid  lg:max-w-[1024px] lg:!grid-cols-2 lg:!items-start lg:!gap-0 lg:rounded-2xl lg:border lg:border-[#EDECEB] lg:bg-white">
              <Item
                remoteItems={basketCheckAvailabilityData?.data.response}
                item={cartItem}
                count={count}
              />

              <CardDivider sectionClassName="lg:hidden" type="section" />
              <div className="w-full ">
                {canSubmit && (
                  <Details
                    calculation={calculation}
                    quantity={count}
                    pending={isIdle}
                  />
                )}
                <FixedBottom className="px-4 lg:static lg:!mx-[24px] lg:!w-auto lg:!border-t-0 lg:px-0 ">
                  <AnalyticsWrapper analytics={['checkout-submit-cart-v1']}>
                    <Button
                      id="checkout-submit-cart-v1"
                      type="black"
                      block
                      onClick={clickHandler}
                      className="lg:!w-full"
                    >
                      ادامه خرید
                    </Button>
                  </AnalyticsWrapper>
                </FixedBottom>
              </div>
            </div>
            <AuthDrawer
              openAuthDrawer={openAuthDrawer && type === 'drawer'}
              handleToggle={handleAuthDrawer}
            />
            <div ref={componentRef} className="hidden w-full lg:!block">
              <AuthModal
                open={openAuthDrawer && type === 'modal'}
                onCancel={handleAuthDrawer}
              />
            </div>
          </div>
        </AnalyticsWrapper>
      </Layout>
      <Footer className="hidden w-full lg:block" offsetMobileNav={false} />
    </>
  );
}
